"use client";

import { LanguageContext } from "@/context/languageContext";
import styles from "@/styles/modules/components/homepage/bookBanner.module.scss";
import { translate } from "@/utils/translate";
import { FormEvent, useContext, useRef, useState, useEffect } from "react";
import { formatDate, formatDateMarriott } from "@/utils/formatDate";
import { useLanguageUrl } from "@/context/languageURLContext";

export default function BookBanner() {
  const { language } = useContext(LanguageContext);
  const checkinRef = useRef<HTMLInputElement>(null);
  const checkoutRef = useRef<HTMLInputElement>(null);
  const guestsRef = useRef<HTMLSelectElement>(null);

  const { url, setUrl } = useLanguageUrl(); // Access context values

  useEffect(() => {
    setUrl(`/${language === "en" ? "de" : "en"}`);
  }, [language, setUrl]);

  const [checkOutDate, setCheckOutDate] = useState<string>("");

  const setupValuesCheckInCheckOut = () => {
    const today = new Date();
    const nextDay = new Date(today);
    const nextYear = new Date(today);

    nextYear.setFullYear(today.getFullYear() + 1);
    nextDay.setDate(nextDay.getDate() + 1); // Set checkout date to the following day

    if (checkoutRef.current) {
      checkoutRef.current.min = today.toISOString().substring(0, 10);
      checkoutRef.current.max = nextYear.toISOString().substring(0, 10);
      checkoutRef.current.value = nextDay.toISOString().substring(0, 10);
    }
    if (checkinRef.current) {
      checkinRef.current.value = today.toISOString().substring(0, 10);
    }
  };

  useEffect(() => {
    setupValuesCheckInCheckOut();
    // Add click event listener to open calendar when clicking anywhere on the input fields
    const handleArrivalClick = () => {
      if (checkinRef.current) {
        checkinRef.current.showPicker();
      }
    };
    const handleDepartureClick = () => {
      if (checkoutRef.current) {
        checkoutRef.current.showPicker();
      }
    };

    if (checkinRef.current) {
      checkinRef.current.addEventListener("click", handleArrivalClick);
    }
    if (checkoutRef.current) {
      checkoutRef.current.addEventListener("click", handleDepartureClick);
    }

    return () => {
      if (checkinRef.current) {
        checkinRef.current.removeEventListener("click", handleArrivalClick);
      }
      if (checkoutRef.current) {
        checkoutRef.current.removeEventListener("click", handleDepartureClick);
      }
    };
  }, []);

  useEffect(() => {
    if (checkoutRef.current) {
      setCheckOutDate(checkoutRef.current?.value);
    }
  }, [checkoutRef.current?.value]);

  const handleCheckinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checkin = new Date(e.target.value);
    const nextDay = new Date(checkin);
    const checkoutMin = new Date(checkin);
    const checkoutMax = new Date(checkin);
    checkoutMax.setFullYear(checkin.getFullYear() + 1);
    nextDay.setDate(checkin.getDate() + 1);

    if (checkoutRef.current) {
      checkoutRef.current.min = formatDate(checkoutMin);
      checkoutRef.current.max = formatDate(checkoutMax);
      checkoutRef.current.value = formatDate(nextDay);
      setCheckOutDate(checkoutRef.current?.value);
    }
  };
  const handleCheckOutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (checkoutRef.current) {
      setCheckOutDate(checkoutRef.current?.value);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const url = `https://www.marriott.com/${language === "de" ? "de/" : ""}`;

    window.open(
      `${url}reservation/availabilitySearch.mi?propertyCode=VIEIR&fromDate=${formatDateMarriott(
        checkinRef.current!.value,
      )}&toDate=${formatDateMarriott(checkoutRef.current!.value)}&roomCount=1&numberOfGuests=${guestsRef.current!.value}`,
      "_blank",
    );
    setupValuesCheckInCheckOut();
  };

  return (
    <section className={styles.bookBanner} data-aos="fade-in">
      <div className={styles.bookBannerContainer}>
        <form onSubmit={handleSubmit}>
          <div className={styles.formElement}>
            <label htmlFor="checkin">{translate["checkIn"][language]}</label>
            <input
              type="date"
              name="checkin"
              id="checkin"
              ref={checkinRef}
              min={new Date().toISOString().substring(0, 10)}
              max={new Date(
                new Date().setFullYear(new Date().getFullYear() + 1),
              )
                .toISOString()
                .substring(0, 10)}
              onChange={handleCheckinChange}
            />
          </div>
          <div className={styles.formElement}>
            <label htmlFor="checkout">{translate["checkOut"][language]}</label>
            <input
              type="date"
              name="checkout"
              id="checkout"
              value={checkOutDate}
              ref={checkoutRef}
              onChange={handleCheckOutChange}
            />
          </div>
          <div className={styles.formElement}>
            <label htmlFor="guests">{translate["guests"][language]}</label>
            <select name="guests" id="guests" ref={guestsRef}>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
              <option value="6">6</option>
              <option value="7">7</option>
              <option value="8">8</option>
            </select>
          </div>
          <div className={styles.formElement}>
            <input type="submit" value={translate["bookNow"][language]} />
          </div>
        </form>
      </div>
    </section>
  );
}
